import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Firefly — a round little lantern bug at dusk, storybook style: a plump
// dusk-blue body with a big friendly face, pale cream wings folded on its
// back, bobble antennae and a big warm amber tail bulb — its lantern. Faces
// +Z, stands on six tiny legs at y = 0. About 0.25 m tall to the antennae.
//
// Rig layout (flat — each named node moves about its own pivot):
//   - "body"   : the blue body + face + antennae. Bobs in idle.
//   - "bulb"   : the amber tail lantern. Pulses (the signature glow).
//   - "wing-l" / "wing-r" : the folded cream wings. Shiver during the glow.
// The six tiny legs are static (unnamed) so the feet stay on the ground.

const BLUE = "#5a6fa8";       // dusk-blue shell
const BLUE_DARK = "#46578c";  // shaded back
const AMBER = "#f5b342";      // the tail lantern
const AMBER_DEEP = "#e08f2c"; // the lantern's warm core ring
const WING = "#f6efe3";       // pale cream wings
const EYE_WHITE = "#f7f3e8";  // eye whites (never pure white)
const PUPIL = "#332b33";      // dark plum pupils
const LEG = "#2c2f36";        // deep charcoal legs + antennae

// The pivots the animations move the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, 0, 0];            // ground contact
const BULB_PIVOT: [number, number, number] = [0, 0.105, -0.115];   // lantern centre
const WING_L_PIVOT: [number, number, number] = [-0.03, 0.165, 0.0];
const WING_R_PIVOT: [number, number, number] = [0.03, 0.165, 0.0];

// One big friendly eye: white ball, dark pupil slightly forward, tiny highlight.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.025, 10, 8), color: EYE_WHITE,
      position: [x, 0.13, 0.082], name: "body" },
    { geometry: new THREE.SphereGeometry(0.0135, 8, 6), color: PUPIL,
      position: [x, 0.13, 0.100], name: "body" },
    { geometry: new THREE.SphereGeometry(0.005, 6, 5), color: WING,
      position: [x + 0.006, 0.137, 0.109], name: "body" },
  ];
}

// One antenna: a thin stalk curving up-forward with a tiny bobble.
function antenna(side: 1 | -1): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.005, 0.007, 0.07, 6), color: LEG,
      position: [side * 0.030, 0.205, 0.06], rotation: [0.45, 0, -side * 0.35], name: "body" },
    { geometry: new THREE.SphereGeometry(0.013, 8, 6), color: LEG,
      position: [side * 0.042, 0.236, 0.077], name: "body" },
  ];
}

// One folded wing: a flattened cream ellipse lying along the back, tail-ward.
function wingPart(side: 1 | -1): Part {
  const name = side > 0 ? "wing-r" : "wing-l";
  return {
    geometry: new THREE.SphereGeometry(0.075, 10, 8), color: WING,
    position: [side * 0.042, 0.175, -0.045],
    rotation: [-0.12, side * 0.28, side * 0.20],
    scale: [0.5, 0.16, 1.0], name,
  };
}

// One tiny leg: a charcoal stub reaching from the belly to the ground.
function leg(side: 1 | -1, z: number): Part {
  return {
    geometry: new THREE.CylinderGeometry(0.010, 0.012, 0.055, 6), color: LEG,
    position: [side * 0.050, 0.026, z], rotation: [0, 0, -side * 0.35],
  };
}

export const firefly = defineModel({
  id: "firefly",
  batchId: "mess",
  name: "Firefly",
  tags: ["mess", "cute", "animated", "creature", "insect", "firefly", "bug", "garden", "night", "glow"],
  summary: "A round dusk-blue bug with cream wings and a big glowing amber tail bulb.",
  description:
    "A storybook firefly: a plump dusk-blue bug with a big friendly face, " +
    "bobble antennae, pale cream wings folded on its back and a big warm " +
    "amber lantern for a tail, standing on six tiny legs. Bobs softly at " +
    "rest (idle) and swells its lantern in a slow heartbeat (glow) while " +
    "the wings shiver with delight.",
  author: "models",
  footprint: 0.18,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── body + face (named, bobs) ────────────────────────────────────────
      // The plump dusk-blue body.
      { geometry: new THREE.SphereGeometry(0.085, 12, 10), color: BLUE,
        position: [0, 0.105, 0.0], scale: [1.0, 0.95, 1.15], name: "body" },
      // A darker cap over the back, under the wings.
      { geometry: new THREE.SphereGeometry(0.070, 10, 8), color: BLUE_DARK,
        position: [0, 0.125, -0.035], scale: [1.05, 0.8, 0.95], name: "body" },
      // The round face mass up front.
      { geometry: new THREE.SphereGeometry(0.062, 12, 10), color: BLUE,
        position: [0, 0.125, 0.045], name: "body" },
      ...eye(0.033), ...eye(-0.033),
      ...antenna(1), ...antenna(-1),

      // ── the lantern (named, pulses) ──────────────────────────────────────
      // The big amber tail bulb tucked against the body's back end...
      { geometry: new THREE.SphereGeometry(0.062, 12, 10), color: AMBER,
        position: [0, 0.105, -0.115], scale: [0.95, 0.9, 1.05], name: "bulb" },
      // ...with a deeper amber ring where it meets the body, like a socket.
      { geometry: new THREE.SphereGeometry(0.055, 10, 8), color: AMBER_DEEP,
        position: [0, 0.105, -0.075], scale: [1.05, 0.95, 0.5], name: "bulb" },
      // A cream hot-spot at the very tip of the lantern.
      { geometry: new THREE.SphereGeometry(0.024, 8, 6), color: WING,
        position: [0, 0.105, -0.168], name: "bulb" },

      // ── folded wings (named, shiver) ─────────────────────────────────────
      wingPart(1), wingPart(-1),

      // ── legs (static) ────────────────────────────────────────────────────
      leg(1, 0.045), leg(1, -0.005), leg(1, -0.055),
      leg(-1, 0.045), leg(-1, -0.005), leg(-1, -0.055),
    ];
  },
  animations: [
    // Idle — a soft dusk bob: the body breathes up and down, the lantern sways
    // along a half-beat behind. Quiet and content.
    {
      id: "idle",
      label: "Idle bob",
      duration: 2.8,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.4, value: [0, 0.012, 0], ease: "sine" },
            { t: 2.8, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "bulb", pivot: BULB_PIVOT,
          position: [
            { t: 0, value: [0, 0.004, 0], ease: "sine" },
            { t: 1.4, value: [0, 0.014, 0], ease: "sine" },
            { t: 2.8, value: [0, 0.004, 0], ease: "sine" },
          ],
        },
        {
          target: "wing-l", pivot: WING_L_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0.05], ease: "sine" },
            { t: 1.4, value: [0, 0, -0.05], ease: "sine" },
            { t: 2.8, value: [0, 0, 0.05], ease: "sine" },
          ],
        },
        {
          target: "wing-r", pivot: WING_R_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, -0.05], ease: "sine" },
            { t: 1.4, value: [0, 0, 0.05], ease: "sine" },
            { t: 2.8, value: [0, 0, -0.05], ease: "sine" },
          ],
        },
      ],
    },

    // Glow — the signature move: the lantern swells in a slow double-beat
    // heartbeat while the wings shiver open with delight.
    {
      id: "glow",
      label: "Lantern glow",
      duration: 2.0,
      loop: true,
      tracks: [
        {
          target: "bulb", pivot: BULB_PIVOT,
          scale: [
            { t: 0, value: 1, ease: "sine" },
            { t: 0.4, value: 1.28, ease: "sine" },
            { t: 0.7, value: 1.08, ease: "sine" },
            { t: 1.0, value: 1.24, ease: "sine" },
            { t: 1.6, value: 1, ease: "sine" },
            { t: 2.0, value: 1, ease: "sine" },
          ],
        },
        {
          target: "wing-l", pivot: WING_L_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.4, value: [0, 0.15, 0.30], ease: "sine" },
            { t: 0.7, value: [0, 0.08, 0.15], ease: "sine" },
            { t: 1.0, value: [0, 0.15, 0.28], ease: "sine" },
            { t: 1.6, value: [0, 0, 0], ease: "sine" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "wing-r", pivot: WING_R_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.4, value: [0, -0.15, -0.30], ease: "sine" },
            { t: 0.7, value: [0, -0.08, -0.15], ease: "sine" },
            { t: 1.0, value: [0, -0.15, -0.28], ease: "sine" },
            { t: 1.6, value: [0, 0, 0], ease: "sine" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        // The body leans back a whisker to show the lantern off.
        {
          target: "body", pivot: BODY_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.7, value: [-0.05, 0, 0], ease: "sine" },
            { t: 1.6, value: [0, 0, 0], ease: "sine" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
